import React, { useState } from 'react';
import { TOUCHPOINT_URL } from '../services/api';
import { LinkEngagement } from '../types';
import { fmtDayTime, fmtDwell, fmtLoad, fmtTime } from '../lib';
import StagePill from './StagePill';
import { Dash, Empty, Th, popover, tr } from './PlatformBits';

const FORM_PILL: Record<string, string> = {
  project_album: 'text-forest bg-sage/30',
  website: 'text-[#2c6479] bg-sky-wash/40',
  welcome_video: 'text-[#2c6479] bg-sky-wash/40',
  welcome_project: 'text-amber-ink bg-gold/15',
  site_visit: 'text-brick bg-brick/10',
};

const FORM_LABEL: Record<string, string> = {
  project_album: 'album',
  website: 'website',
  welcome_video: 'welcome video',
  welcome_project: 'welcome project',
  villa_style: 'villa style',
  site_visit: 'site visit',
  watch_construction_update: 'construction update',
  watch_construction_update_latest: 'construction update',
};

/** One muted line of visit context: device · city, country · tz · lang · via ref.
 *  tz shown only when it isn't IST — that's the NRI flag. */
const visitSummary = (v: Record<string, string> | null): React.ReactNode => {
  if (!v) return null;
  const device = v.device
    ? v.device.startsWith('android:') ? v.device.slice(8) : v.device
    : null;
  const loc = [v.city, v.country].filter(Boolean).join(', ');
  // Browsers report either name for IST — both are home, not NRI.
  const abroad = v.tz && v.tz !== 'Asia/Kolkata' && v.tz !== 'Asia/Calcutta';
  const parts = [
    device,
    loc || null,
    v.lang || null,
    v.ref ? `via ${v.ref.replace(/^(l\.|www\.)/, '')}` : null,
  ].filter(Boolean);
  if (!parts.length && !abroad) return null;
  return (
    <div className="text-[11px] text-graphite whitespace-nowrap mt-0.5 opacity-90">
      {abroad && <span className="text-amber-ink font-semibold">{v.tz} · </span>}
      {parts.join(' · ')}
    </div>
  );
};

/** Rows with no per-session tag: the link's own provenance if it has one;
 *  a lead row predating src shows who minted it; a visitor with nothing at
 *  all — no UTM, no referrer, no sender — is direct traffic. */
const legacySourceLabel = (row: LinkEngagement): string =>
  row.source_label || row.campaign_name || (row.lead_id ? row.created_by : 'direct');

/** Hover card on the Load cell: per-frame views / dwell / median load, in
 *  album order. Fixed-positioned so the table's overflow-x container can't
 *  clip it; top clamped to keep long albums inside the viewport. */
const FrameStatsCard: React.FC<{ row: LinkEngagement; x: number; y: number }> = ({ row, x, y }) => {
  const estHeight = 86 + row.frame_stats.length * 21;
  return (
    <div
      className={`fixed z-50 ${popover} px-3.5 py-2.5 pointer-events-none`}
      style={{
        left: Math.min(x + 14, window.innerWidth - 290),
        top: Math.max(8, Math.min(y + 14, window.innerHeight - estHeight - 8)),
      }}
    >
      <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite mb-1.5">Frame timings · {row.lead_name || 'Anonymous visitor'}</div>
      <table className="border-collapse">
        <thead>
          <tr>
            {['Frame', 'Views', 'Dwell', 'Buffer', 'To play', 'Stalls'].map((h) => (
              <th key={h} className="text-left text-[10px] uppercase tracking-[0.06em] font-semibold text-graphite pr-4 pb-1 border-b border-charcoal/10 last:pr-0">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {row.frame_stats.map((f) => (
            <tr key={f.frame} className="text-[12px]">
              <td className="pr-4 py-0.5 font-semibold whitespace-nowrap">{f.frame}</td>
              <td className="pr-4 py-0.5 num">{f.views}</td>
              <td className="pr-4 py-0.5 num">{fmtDwell(f.dwell_ms)}</td>
              <td className={`pr-4 py-0.5 num whitespace-nowrap ${f.buffer_ms_median != null && f.buffer_ms_median >= 3000 ? 'text-brick font-semibold' : ''}`}>
                {fmtLoad(f.buffer_ms_median)}
              </td>
              <td className="pr-4 py-0.5 num whitespace-nowrap">{fmtLoad(f.load_ms_median)}</td>
              <td className={`py-0.5 num whitespace-nowrap ${f.stalls > 0 ? 'text-brick font-semibold' : ''}`}>
                {f.stalls > 0 ? `${f.stalls} · ${fmtLoad(f.stall_ms)}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-graphite mt-1.5">
        buffer = time to playable · to play = until playback · stalls = mid-play rebuffers
      </div>
    </div>
  );
};

/** The links table — bare, the caller puts it in a Section. `albumColumns`:
 *  frames, dwell, load, likes, brochure, video are album facts; a
 *  website-only view drops them rather than showing a row of dashes. */
const EngagementTable: React.FC<{ links: LinkEngagement[]; showSource?: boolean; albumColumns?: boolean }> = ({
  links, showSource = false, albumColumns = true,
}) => {
  const [tip, setTip] = useState<{ row: LinkEngagement; x: number; y: number } | null>(null);
  const headers = [
    'Lead', 'Form',
    ...(showSource ? ['Source'] : []),
    'Opens',
    ...(albumColumns ? ['Frames', 'Dwell', 'Load', 'Liked / shared', 'Brochure'] : []),
    'CTA',
    ...(albumColumns ? ['Video'] : []),
    'Submitted',
  ];
  return (
    <>
      <table className="w-full min-w-[860px] border-collapse">
        <thead>
          <tr>{headers.map((h) => <Th key={h} left>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {links.length === 0 && <Empty cols={headers.length}>No link activity.</Empty>}
          {links.map((l) => (
            <tr key={l.link_id} className={`${tr} hover:bg-charcoal/[0.025] transition-colors`}>
              <td className="px-3 py-3">
                {/* Anonymous album visitors (ad traffic) have no lead — there is
                    nothing to link to, so render a plain label instead of a
                    dead /leads/null link. */}
                {l.lead_id ? (
                  <a
                    href={`${TOUCHPOINT_URL}/leads/${l.lead_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-charcoal whitespace-nowrap hover:text-forest hover:underline underline-offset-2"
                  >
                    {l.lead_name || 'Unknown'} <span className="text-graphite/60 text-[11px]">↗</span>
                  </a>
                ) : (
                  <span className="font-semibold whitespace-nowrap text-graphite">Anonymous visitor</span>
                )}
                <div className="text-[11.5px] text-graphite num whitespace-nowrap mt-0.5">{l.lead_phone || '—'}</div>
                {l.lead_id && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <StagePill stage={l.stage} />
                    {l.claimed_at && (
                      <span className="text-[11px] text-forest font-semibold whitespace-nowrap num">
                        became a lead {fmtDayTime(l.claimed_at)}
                      </span>
                    )}
                  </div>
                )}
                {visitSummary(l.last_visit)}
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${FORM_PILL[l.form_type] || 'text-graphite bg-charcoal/[0.06]'}`}>
                  {FORM_LABEL[l.form_type] || l.form_type}
                </span>
              </td>
              {showSource && (
                <td className="px-3 py-3 text-[12px] text-graphite max-w-[200px]">
                  {/* Labels come typed from core (app/constants/sources.py).
                      The portal never parses a tag — an `ad:` that became
                      `a:` here once truncated every ad id to 8 chars. */}
                  {l.source_labels.length > 0
                    ? l.source_labels.map((label, i) => (
                        <span key={l.sources[i] ?? label} className="inline-block mr-1 mb-1 px-2 py-0.5 rounded-full bg-charcoal/[0.06] text-charcoal text-[11px] font-medium whitespace-nowrap">
                          {label}
                        </span>
                      ))
                    : legacySourceLabel(l)}
                </td>
              )}
              <td className="px-3 py-3 whitespace-nowrap num">
                {l.open_count
                  ? <><span className="font-semibold">{l.open_count}×</span>{l.last_opened_at && <div className="text-[11px] text-graphite mt-0.5">last {fmtDayTime(l.last_opened_at)}</div>}</>
                  : <Dash />}
              </td>
              {albumColumns && (<>
              <td className="px-3 py-3 whitespace-nowrap num">
                {l.distinct_frames > 0
                  ? <span className={l.distinct_frames >= 12 ? 'text-forest font-semibold' : ''}>{l.distinct_frames}</span>
                  : l.dwell_ms_total > 0 && l.form_type === 'project_album'
                    ? <span className="text-graphite text-[11.5px] italic">— (timed)</span>
                    : <Dash />}
              </td>
              <td className="px-3 py-3 whitespace-nowrap num">{fmtDwell(l.dwell_ms_total)}</td>
              <td
                className="px-3 py-3 whitespace-nowrap num"
                onMouseEnter={(e) => l.frame_stats.length > 0 && setTip({ row: l, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTip(null)}
              >
                {/* Buffer median (pure network) when captured; older rows fall
                    back to time-to-play, italicized as the weaker signal. */}
                {(l.buffer_ms_median ?? l.load_ms_median) != null
                  ? (
                    <span className={`underline decoration-dotted decoration-graphite/60 underline-offset-4 cursor-help ${(l.buffer_ms_median ?? l.load_ms_median)! >= 3000 ? 'text-brick font-semibold' : ''} ${l.buffer_ms_median == null ? 'italic text-graphite' : ''}`}>
                      {fmtLoad(l.buffer_ms_median ?? l.load_ms_median)}
                    </span>
                  )
                  : l.frame_stats.length > 0
                    ? <span className="underline decoration-dotted decoration-graphite/60 underline-offset-4 cursor-help text-graphite">—</span>
                    : <Dash />}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-[12.5px]">
                {l.likes === 0 && l.shares === 0 && <Dash />}
                {l.likes > 0 && (
                  <span className="text-brick font-semibold">♥ {l.liked_frames.join(', ') || l.likes}</span>
                )}
                {l.likes > 0 && l.shares > 0 && <span className="text-graphite"> · </span>}
                {l.shares > 0 && (
                  <span className="text-forest font-semibold">↗ {l.shared_frames.join(', ') || l.shares}</span>
                )}
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                {l.brochure_opens > 0
                  ? <span className="text-forest font-semibold text-[13px]">✓{l.brochure_opens > 1 && ` ×${l.brochure_opens}`}</span>
                  : <Dash />}
              </td>
              </>)}
              <td className="px-3 py-3 whitespace-nowrap">
                {/* cta_clicked_at, not button_clicked_at: the scalar is the
                    link's first tap ever, so it would credit this campaign
                    with a tap from an earlier one. */}
                {l.cta_clicked_at
                  ? (
                    <span className="text-forest font-semibold text-[12.5px] num">
                      ✓ {fmtTime(l.cta_clicked_at)}
                      {l.cta_clicks > 1 && <span className="font-normal"> ×{l.cta_clicks}</span>}
                      {l.cta_intent && <span className="font-normal text-graphite"> · {l.cta_intent.replace(/_/g, ' ')}</span>}
                    </span>
                  )
                  : <Dash />}
              </td>
              {albumColumns && (
                <td className="px-3 py-3 whitespace-nowrap num">
                  {l.video_pct_watched ? `${l.video_pct_watched}%` : <Dash />}
                </td>
              )}
              <td className="px-3 py-3 whitespace-nowrap">
                {l.submitted_at
                  ? <span className="text-forest font-semibold text-[12.5px] num">✓ {fmtDayTime(l.submitted_at)}</span>
                  : <Dash />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {tip && <FrameStatsCard row={tip.row} x={tip.x} y={tip.y} />}
    </>
  );
};

export default EngagementTable;
